import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@phosphor-icons/react';

const About = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const whyChoosePoints = [
    { title: t('qualityAssurance'), desc: t('qualityText') },
    { title: t('advancedTech'), desc: t('techText') },
    { title: t('skilledCraft'), desc: t('craftText') },
    { title: t('clientCentric'), desc: t('clientText') },
    { title: t('timelyDelivery'), desc: t('deliveryText') },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
              {t('about')}
            </p>
            <h1 className={`text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-none mb-6 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`} data-testid="about-title">
              {t('aboutTitle')}
            </h1>
            <p className={`text-xl leading-relaxed text-gray-700 ${
              isRTL ? 'font-arabic' : ''
            }`}>
              {t('aboutDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Profile */}
      <section className="section-padding bg-white" data-testid="company-profile">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <img
                src="https://images.unsplash.com/photo-1659362549741-c32157cc71f4"
                alt="Quarry"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className={`text-3xl sm:text-4xl font-light tracking-tight mb-6 ${
                isRTL ? 'font-arabic' : 'font-heading'
              }`}>
                {t('ourVision')}
              </h2>
              <p className={`text-base leading-relaxed text-gray-700 mb-6 ${
                isRTL ? 'font-arabic' : ''
              }`}>
                {t('visionText')}
              </p>
              <p className={`text-base leading-relaxed text-gray-700 ${
                isRTL ? 'font-arabic' : ''
              }`}>
                {isRTL 
                  ? 'نهدف إلى تعزيز الشراكات طويلة الأمد المبنية على النزاهة والاحترافية والمسؤولية البيئية - لتشكيل مساحات أنيقة تصمد أمام اختبار الزمن وتساهم بشكل إيجابي في البيئة المبنية.'
                  : 'We aim to foster long-term partnerships built on integrity, professionalism, and environmental responsibility – shaping elegant spaces that stand the test of time and contribute positively to the built environment.'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-4 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`}>
              {t('whyChooseUs')}
            </h2>
          </div>
          <div className="space-y-8">
            {whyChoosePoints.map((point, index) => (
              <div
                key={index}
                className="bg-white p-8 border border-black/5 hover:border-[#C4A661] transition-all duration-300"
                data-testid={`why-choose-card-${index}`}
              >
                <div className="flex items-start space-x-4">
                  <CheckCircle size={28} weight="fill" className="text-[#C4A661] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className={`text-2xl font-medium mb-3 ${
                      isRTL ? 'font-arabic' : 'font-heading'
                    }`}>
                      {point.title}
                    </h3>
                    <p className={`text-base text-gray-700 leading-relaxed ${
                      isRTL ? 'font-arabic' : ''
                    }`}>
                      {point.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-[#0F0F0F] text-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {[
              { num: '1000+', label: isRTL ? 'المشاريع المنجزة' : 'Projects Completed' },
              { num: '20+', label: isRTL ? 'سنوات من الخبرة' : 'Years Experience' },
              { num: '50+', label: isRTL ? 'محترفون ماهرون' : 'Skilled Professionals' },
              { num: '100%', label: isRTL ? 'رضا العملاء' : 'Client Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className={`text-5xl font-light mb-2 text-[#C4A661] ${
                  isRTL ? 'font-arabic' : 'font-heading'
                }`}>
                  {stat.num}
                </div>
                <div className={`text-sm text-gray-400 uppercase tracking-wider ${
                  isRTL ? 'font-arabic' : ''
                }`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
